import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Order, Orders, Summary } from "../pages/SalesPage/SalesPageInterface";
import { getOrdersByUser } from "../services/orders/orderAxios";
import { FilterOrdersType } from "../components/buttons/ButtonFilterTypes";
import { LogisticState } from "../types/order/logisticState";
import { PaidState } from "../types/order/paidState";
import { formatDateToISOString } from "../utils/formatters";
import { QueryFiltersParams } from "../services/orders/orderAxios";
import { Product } from "../components/products/ProductInterface";
import { getProducts } from "../services/products/productAxios";
import { handleApi } from "../utils/handleApi";


export const useSalesData = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [orders, setOrders] = useState<Orders>();
  const [filteredOrders, setFilteredOrders] = useState<Orders>();
  const [isLoading, setIsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState<FilterOrdersType>("all");
  const [activeDateFilter, setActiveDateFilter] = useState<{
    label: string;
    startDate: Date;
    endDate: Date;
  } | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [activeProductFilters, setActiveProductFilters] = useState<{
    id: string;
    name: string;
  }[]>([]);

  const getQueryFiltersFromURL = (): QueryFiltersParams => {
    return {
      date_init: searchParams.get('date_init') || null,
      date_end: searchParams.get('date_end') || null,
      product_ids: searchParams.get('product_ids')?.split(',').filter(Boolean) || [],
    };
  };

  const loadProducts = async () => {
    const { res, error } = await handleApi(getProducts());
    if (error) return;
    setProducts(res || []);
  };

  useEffect(() => {
    loadProducts();
  }, []);

  useEffect(() => {
    const dateInit = searchParams.get('date_init');
    const dateEnd = searchParams.get('date_end');
    const filterLabel = searchParams.get('filter_label');
    
    if (dateInit && dateEnd) {
      setActiveDateFilter({
        label: filterLabel || `${dateInit} - ${dateEnd}`,
        startDate: new Date(dateInit),
        endDate: new Date(dateEnd),
      });
    }
    
    const productIds = searchParams.get('product_ids')?.split(',').filter(Boolean) || [];
    const productNames = searchParams.get('product_names')?.split('||').filter(Boolean) || [];
    
    if (productIds.length > 0 && productNames.length > 0) {
      const activeProducts = productIds.map((id, index) => ({
        id,
        name: productNames[index] || id,
      }));
      setActiveProductFilters(activeProducts);
    }
  }, []);

  useEffect(() => {
    const filters = getQueryFiltersFromURL();
    loadOrders(filters);
  }, [searchParams]);

  useEffect(() => {
    if (!orders?.orders) return;
    
    const filtered =
      activeFilter === "all"
        ? orders.orders
        : activeFilter === "unfulfilled"
        ? orders.orders.filter((o) => o.logistic_state === LogisticState.CREATED)
        : activeFilter === "fulfilled"
        ? orders.orders.filter((o) => o.logistic_state === LogisticState.IN_PROGRESS)
        : activeFilter === "paid"
        ? orders.orders.filter((o) => o.payment_state === PaidState.PAID)
        : activeFilter === "pending"
        ? orders.orders.filter((o) => o.payment_state === PaidState.PENDING)
        : orders.orders;

    setFilteredOrders({ orders: filtered, summary: calculateSummary(filtered) });
  }, [orders, activeFilter]);

  const calculateSummary = (ordersList: Order[]) => {
    const total_value_with_discount_summary: number = ordersList.reduce(
      (summary, order) => {
        return summary + order.total_value_with_discount;
      },
      0
    );

    const total_value_summary: number = ordersList.reduce((summary, order) => {
      return summary + order.total_value;
    }, 0);

    const total_units_summary: number = ordersList.reduce((summary, order) => {
      return summary + order.total_units;
    }, 0);

    const total_buyers_summary = ordersList.filter(
      (order, index, self) =>
        index ===
        self.findIndex((t) => t.customer_email === order.customer_email)
    ).length;

    const response: Summary = {
      total_buyers: total_buyers_summary,
      total_units: total_units_summary.toFixed(0),
      total_value: total_value_summary.toFixed(2),
      total_value_with_discount: total_value_with_discount_summary.toFixed(2),
    };
    return response;
  };

  // TODO: ESTABLECER INTERFAZ PARA RESPUESTA DE API
  const normalizeOrders = (res: any): Orders => {
    return res.orders
      ? res
      : {
          orders: [],
          summary: {
            total_value: res.total_value?.toFixed(2) || "0.00",
            total_value_with_discount: res.total_value_with_discount?.toFixed(2) || "0.00",
            total_units: res.total_units?.toFixed(0) || "0",
            total_buyers: res.total_buyers || 0,
          },
        };
  };

  // TODO: PASAR A HANDLE API
  const loadOrders = async (queryFilters: QueryFiltersParams) => {
    setIsLoading(true);
    try {
      const res = await getOrdersByUser(queryFilters);
      const data = normalizeOrders(res);
      setOrders(data);
      // No actualizar filteredOrders aquí, el useEffect se encargará de aplicar el filtro activo
    } catch (err) {
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const filterOrders = (filter: FilterOrdersType) => {
    if (!orders?.orders) return;
    setActiveFilter(filter);

    const filtered =
      filter === "all"
        ? orders.orders
        : filter === "unfulfilled"
        ? orders.orders.filter((o) => o.logistic_state === LogisticState.CREATED)
        : filter === "fulfilled"
        ? orders.orders.filter((o) => o.logistic_state === LogisticState.IN_PROGRESS)
        : filter === "paid"
        ? orders.orders.filter((o) => o.payment_state === PaidState.PAID)
        : filter === "pending"
        ? orders.orders.filter((o) => o.payment_state === PaidState.PENDING)
        : orders.orders;

    setFilteredOrders({ orders: filtered, summary: calculateSummary(filtered) });
  };

  const filterByDate = async (start: Date, end: Date, label: string) => {
    const dateInit = formatDateToISOString(start);
    const dateEnd = formatDateToISOString(end);
    
    const newParams = new URLSearchParams(searchParams);
    newParams.set('date_init', dateInit);
    newParams.set('date_end', dateEnd);
    newParams.set('filter_label', label);
    setSearchParams(newParams);
    
    setActiveDateFilter({ label, startDate: start, endDate: end });
  };

  const filterByProductId = async (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    
    const newParams = new URLSearchParams(searchParams);
    const currentProducts = newParams.get('product_ids')?.split(',').filter(Boolean) || [];
    
    if (!currentProducts.includes(productId)) {
      currentProducts.push(productId);
      newParams.set('product_ids', currentProducts.join(','));
      
      const currentNames = newParams.get('product_names')?.split('||').filter(Boolean) || [];
      currentNames.push(product.name);
      newParams.set('product_names', currentNames.join('||'));
      
      setSearchParams(newParams);
      
      setActiveProductFilters([...activeProductFilters, { id: productId, name: product.name }]);
    }
  }

  const removeProductFilter = (productId: string) => {
    const newParams = new URLSearchParams(searchParams);
    const currentProducts = newParams.get('product_ids')?.split(',').filter(Boolean) || [];
    const currentNames = newParams.get('product_names')?.split('||').filter(Boolean) || [];
    
    const indexToRemove = currentProducts.indexOf(productId);
    if (indexToRemove > -1) {
      currentProducts.splice(indexToRemove, 1);
      currentNames.splice(indexToRemove, 1);
      
      if (currentProducts.length > 0) {
        newParams.set('product_ids', currentProducts.join(','));
        newParams.set('product_names', currentNames.join('||'));
      } else {
        newParams.delete('product_ids');
        newParams.delete('product_names');
      }
      
      setSearchParams(newParams);
      
      setActiveProductFilters(activeProductFilters.filter(p => p.id !== productId));
    }
  }

  const clearDateFilter = async () => {
    setActiveDateFilter(null);
    
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('date_init');
    newParams.delete('date_end');
    newParams.delete('filter_label');
    setSearchParams(newParams);
  };

  const deleteOrder = (orderId: string) => {
    if (!orders) return;
    const updated = orders.orders.filter((o) => o.id.toString() !== orderId);
    const newOrders = { orders: updated, summary: calculateSummary(updated) };
    setOrders(newOrders);
  };

  return {
    orders,
    filteredOrders,
    isLoading,
    activeFilter,
    activeDateFilter,
    activeProductFilters,
    calculateSummary,
    filterOrders,
    filterByDate,
    clearDateFilter,
    deleteOrder,
    filterByProductId,
    removeProductFilter,
    products,
  };
};
